import { Block, getEditorBlockConfig } from "@babackapp/editor-blocks-library/blocks";
import type { ParcelDto } from "@babackapp/private-api-client/types";
import useBlockFormApi from "../hooks/useBlockFormApi";
import BlockContainer from "./block-container";
import { useEditorContext } from "./editor";
import { useShop } from "./shop-provider";
import { createFormatter, useVariablesValues } from "@babackapp/variables-registry";
import React, { useMemo } from "react";
import { BlockType } from "../types";


interface BlockRendererProps {
  section: "before" | "inside" | "after";
  index: number;
  blockId: string;
  block: Block<BlockType>;
  platform: "email" | "web";
}


const MemoizedBlockRenderer = React.memo(
  function BlockRenderer({
    section,
    index,
    block,
    platform,
  }: BlockRendererProps) {
    const { currentLocale, theme, variables, defaultLocale, editorMode } =
      useEditorContext();
    const shop = useShop();
    const blockType = block?.type as BlockType;
    const BlockComponent = getEditorBlockConfig(blockType);
    const formAPI = useBlockFormApi(section, index);

    // Get variable values from the dictionary (includes user edits for preview)
    const { variablesValues } = useVariablesValues();
    const formatter = useMemo(
      () => createFormatter(variablesValues),
      [variablesValues],
    );

    // Select the appropriate editor component based on platform
    const EditorComponent =
      platform === "email"
        ? BlockComponent?.EmailEditor
        : BlockComponent?.WebEditor;

    if (!blockType || !EditorComponent || !block) {
      return <div>Unknown block type</div>;
    }

    return (
      <BlockContainer section={section} index={index} block={block}>
        <EditorComponent
          variables={variables}
          theme={theme}
          locale={currentLocale}
          defaultLocale={defaultLocale}
          formatter={formatter}
          shop={shop}
          section={section}
          block={block as never}
          index={index}
          formAPI={formAPI}
          parcel={parcelMock}
          editorMode={editorMode}
        />
      </BlockContainer>
    );
  },
  (prevProps, nextProps) => {
    // Re-render if section, index, blockId, or platform changes
    // blockId ensures we re-render when blocks are reordered or removed
    // return false;
    return (
      prevProps.section === nextProps.section &&
      prevProps.index === nextProps.index &&
      prevProps.blockId === nextProps.blockId &&
      prevProps.platform === nextProps.platform &&
      JSON.stringify(prevProps.block) === JSON.stringify(nextProps.block)
    );
  },
);

export default function EditorBlockRenderer(props: BlockRendererProps) {
  return <MemoizedBlockRenderer {...props} />;
}
const estimationDeliveryDateMock: ParcelDto["estimatedDeliveryDateWindow"] = {
  exactDeliveryDay: new Date().setDate(new Date().getDate() + 5),
  minimumDeliveryDate: new Date().setDate(new Date().getDate() + 3),
  maximumDeliveryDate: new Date().setDate(new Date().getDate() + 7),
};

const parcelMock: ParcelDto = {
  id: "mock-parcel-1",
  orderId: "mock-order-1",
  trackingId: "TRACK123456",
  fulfillmentId: "mock-fulfillment-1",
  orderNumber: "#1001",
  shopId: "mock-shop-1",
  customerEmail: "customer@example.com",
  customerLocale: "fr",
  carrierCode: "UPS",
  trackingNumbers: ["TRACK123456"],
  createdAt: Date.now(),
  updatedAt: Date.now(),
  version: 1,
  previousEmails: [],

  destination: {
    firstName: "Guillaume",
    address1: "123 Main St",
    phone: "555-555-5555",
    city: "New York",
    zip: "10001",
    province: "NY",
    country: "US",
    lastName: "B",
    company: "Baback",
    name: "Guillaume B",
    countryCode: "US",
    provinceCode: "NY",
  },
  estimatedDeliveryDateWindow: estimationDeliveryDateMock,
  lineItems: [
    {
      name: "Test Product",
      imageUrl:
        "https://cdn.shopify.com/s/files/1/0557/2211/5151/files/pexels-cottonbro-studio-9834872.jpg?v=1709650972",
      sku: "12345678901",
      quantity: 1,
      price: 100,
    },
    {
      name: "Test Product 2",
      imageUrl:
        "https://cdn.shopify.com/s/files/1/0557/2211/5151/files/pexels-cottonbro-studio-9834872.jpg?v=1709650972",
      sku: "12345678902",
      quantity: 1,
      price: 100,
    },
  ],

  events: [
    {
      status: "PRE_TRANSIT" as const,
      createdAt: new Date("2024-01-15T08:00:00Z").getTime(),
      label: "Order received",
      notifications: [],
    },
    {
      status: "IN_TRANSIT" as const,
      createdAt: new Date("2024-01-16T10:30:00Z").getTime(),
      label: "Package in transit",
      notifications: [],
    },
    {
      status: "OUT_FOR_DELIVERY" as const,
      createdAt: new Date("2024-01-17T09:15:00Z").getTime(),
      label: "Out for delivery",
      notifications: [],
    },
  ],
  rawCarrierEvents: [],
} as ParcelDto;
